import { type DocumentType, modelOptions, PropType, post, pre, prop, type Ref, Severity } from "@typegoose/typegoose";
import { SensorConfigurations } from "../../../Configurations/SensorConfigurations/SensorConfiguration.js";
import { validateAllSensorParameters } from "../../../Configurations/SensorConfigurations/SensorConfigurationParameterHelpers.js";
import type { SensorType } from "../../../shared.js";
import { RecentSensorDataPoint } from "../../commonSubSchemas/recentSensorDataPoint.js";
import { VertexInfo } from "../../commonSubSchemas/vertexInfo.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, type SchemaConstructorArgs } from "../../schemaBase.js";
import type { Device } from "../device.js";
import { minuteMs } from "../../../timeConstants.js";
import {
    validate,
    getZodSchemaFromClass,
    SensorTypeString,
    ObjectIdString,
    milliseconds,
    valueRangeArray,
} from "../../../zod-decorator/index.js";
import { z } from "zod";
import { type DtoValidatorTypes } from "../../validatableClass.js";
import type { SensorSpecifierFieldsWithDefaults, SensorSpecifierSchemaTypes } from "../../types/sensorSpecifier.d.js";

const SCHEMA_KEY = "SensorSpecifier" as const;
// Forward declarations to avoid circular dependencies
type Zone = any;

@modelOptions({ schemaOptions: { ...getDefaultSchemaOptions(SCHEMA_KEY) } })
/**
 * Post-init hook to store the original sensorParameters state for change detection.
 * This runs after a document is initialized from the database.
 * Uses $locals (Mongoose's recommended property for temporary document-level data).
 * Note: In Typegoose 12, 'init' must be passed as a string, not an array.
 */
@post<SensorSpecifier>("init", function (this: DocumentType<SensorSpecifier>) {
    // Store the current sensorParameters in $locals for later comparison
    this.$locals.originalSensorParameters = JSON.stringify(this.sensorParameters);
})
/**
 * Post-query hook to store the original sensorParameters state for documents loaded via queries.
 * Handles find (array), findOne, and findOneAndUpdate results.
 */
@post<SensorSpecifier>(["find", "findOne", "findOneAndUpdate"], async function (this, doc) {
    if (Array.isArray(doc)) {
        for (const item of doc) {
            if (item?.$locals && item.sensorParameters) {
                item.$locals.originalSensorParameters = JSON.stringify(item.sensorParameters);
            }
        }
    } else if (doc?.$locals && doc.sensorParameters) {
        doc.$locals.originalSensorParameters = JSON.stringify(doc.sensorParameters);
    }
})
/**
 * Pre-save hook to detect changes to sensorParameters (a mixed/Object type).
 * Mongoose cannot automatically detect changes to mixed types, so we compare
 * the current state with the original and mark as modified if changed.
 */
@pre<SensorSpecifier>("save", async function saveDevice(this: DocumentType<SensorSpecifier>) {
    // Check if sensorParameters have changed by comparing with stored original
    const original = this.$locals?.originalSensorParameters;

    // If this is a new document (never saved), mark as modified
    if (this.isNew && this.sensorParameters) {
        this.markModified("sensorParameters");
        return;
    }

    // If baseline exists and current value differs, mark as modified
    if (original !== undefined && JSON.stringify(this.sensorParameters) !== original) {
        this.markModified("sensorParameters");
    }
    // Note: If original is undefined (e.g., due to projection excluding sensorParameters),
    // we don't automatically mark as modified to avoid incorrect behavior.
    // If the field was intentionally modified, the caller should use markModified explicitly.
})
/**
 * Post-save hook to update the baseline after a successful save.
 * This prevents unnecessary markModified calls on subsequent saves of the same document instance.
 * Only updates baseline if sensorParameters was actually loaded (not excluded by projection).
 */
@post<SensorSpecifier>("save", function (this: DocumentType<SensorSpecifier>) {
    // Only update baseline if we know the field was loaded (baseline was set during init)
    // or if the field is now explicitly set. This prevents incorrect baseline updates when
    // sensorParameters is excluded from a query via projection.
    const wasLoadedFromDb = this.$locals.originalSensorParameters !== undefined;
    const isExplicitlySet = "sensorParameters" in this && this.sensorParameters !== undefined;

    if (wasLoadedFromDb || isExplicitlySet) {
        if (this.sensorParameters) {
            this.$locals.originalSensorParameters = JSON.stringify(this.sensorParameters);
        } else {
            this.$locals.originalSensorParameters = undefined;
        }
    }
})
/**
 * Make sure the sensor parameters are set correctly if they have been modified.
 * This pre-save hook validates that:
 * 1. The sensorType has a valid configuration
 * 2. All required parameters exist and pass their validation rules
 * 3. No unknown parameters are present
 *
 * If the sensor parameters and sensor type were modified, this will prevent saving to protect database
 * integrity. If they were not modified (meaning the db has already lost integrity), then it simply reports
 * the error.
 */
@pre<SensorSpecifier>("save", async function saveDevice(this: DocumentType<SensorSpecifier>) {
    const throwSuitableError = (errorMessage: string) => {
        const errorHeader = `Error saving sensor specifier on device ${
            (this.$parent() as any)?.node_id
        }, sensor index ${this.sensorIndex}\n`;
        const messageIfAborting = `Aborting device save operation to avoid entering illegal state\n`;
        const messageIfContinuing = `Continuing device save operation since device was already in this illegal state\n`;

        // we only want to abort the save if we are modifying the sensor params or sensor type.
        // if the sensor params and sensor type are unmodified, aborting this operation will not help
        if (this.sensorType && this.isModified(["sensorParameters", "sensorType"])) {
            throw new Error(errorHeader + messageIfAborting + errorMessage);
        }
        // For now, we'll just log the error instead of using Logger
        console.error(errorHeader + messageIfContinuing + errorMessage);
    };
    const sensorConfig = SensorConfigurations[this.sensorType];

    // Basic validation - we'll need to import proper validation when available
    if (!this.sensorType) {
        throwSuitableError(`SensorType is required but was not provided!`);
        // the above might not error out, but might instead simply output an error message if we aren't modifying
        // the sensors in this update.
        // This means we need to return otherwise the below code *will* generate an actual error.
        return;
    }

    // Perform full sensor parameter validation using validateAllSensorParameters and SensorConfigurations.
    const sensorParameterValidation = validateAllSensorParameters(this.sensorParameters, sensorConfig);
    if (!sensorParameterValidation[0]) {
        throwSuitableError(`Error in sensor parameters: ${sensorParameterValidation[1]}`);
    }

    // try to autofill the sensor index correctly
    if ((this.$parent() as any)?.connectedSensors) {
        const hypotheticalSensorIndex = (this.$parent() as any)?.connectedSensors.indexOf(this);
        if (hypotheticalSensorIndex !== -1) {
            this.sensorIndex = hypotheticalSensorIndex;
        } else {
            const parent = this.$parent() as any;
            const parentId = parent?.node_id ?? "unknown";
            const connectedSensors = parent?.connectedSensors ?? [];
            const sensorSummary = this.sensorType ? `type: ${this.sensorType}` : "unknown type";
            console.warn(
                `Failed to assign sensor index for sensor (${sensorSummary}) on parent node_id: ${parentId}. ` +
                    `connectedSensors length: ${connectedSensors.length}. ` +
                    `Sensor object not found in parent's connectedSensors array.`,
            );
        }
    } else {
        const parent: { node_id?: string } = this.$parent && (this.$parent() as DocumentType<Device>);
        const parentId = parent?.node_id ? ` (node_id: ${parent.node_id})` : "";
        console.warn(
            `Parent object${parentId} does not have a 'connectedSensors' property while assigning sensor index for sensor at index ${this.sensorIndex}.`,
        );
    }
})
@post<SchemaBase<SensorSpecifier>>(...addSchemaKeyHook(SCHEMA_KEY))
export class SensorSpecifier extends SchemaBase<SensorSpecifier, SensorSpecifierFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({
        childClass: VertexInfo,
        childClassTransform: (object) => object.optional(),
    })
    @prop({ type: () => VertexInfo, required: false })
    public position?: VertexInfo;

    @validate({ zodValidator: SensorTypeString })
    @prop({ type: () => String, required: true })
    public sensorType!: SensorType;

    // will always be filled out before a save
    @validate({ zodValidator: z.number().optional(), responseOnly: true })
    @prop({ type: () => Number, required: false })
    public sensorIndex?: number;

    /**
     * This object specifies any information that is necessary about this particular sensor
     * as compared to other sensors of it's sensorType. These options are typically used for
     * converting values from raw readings to user-viewable values.
     *
     * - This object must be present so we are explicit about sensor parameters
     * - It is validated a in the pre-save and post-find hooks to ensure that the parameters
     *   present match the requirements based on the sensor type
     * - It must default to {} due to some funny stuff around saving and loading empty objects
     *   from mongo.
     * - Sensor parameters are not presently tracked over time (as of 2025) and changes to them
     *   will not propagate backwards in the data
     */
    @validate({ zodValidator: z.record(z.string(), z.union([z.number(), z.string()])).optional() })
    @prop({
        type: () => Object,
        allowMixed: Severity.ALLOW,
        required: true,
        default: {},
    })
    public sensorParameters: Record<string, number | string> = {};

    /**
     * We want to get rid of "default" as a valid option for this as soon as possible and ensure
     * zones are set explicitly
     */
    @validate({
        zodValidator: z.array(z.union([ObjectIdString, z.literal("default")])).optional(),
    })
    @prop({ type: () => String, required: true, default: ["default"] }, PropType.ARRAY)
    public connectedZones: (Ref<Zone> | "default")[] = ["default"];

    /**
     * This is responsible for threshold protection, generates SMS alerts
     * must be empty or contain exactly 2 numbers
     */
    @validate({
        zodValidator: valueRangeArray.optional(),
    })
    @prop({ type: () => Number, required: false }, PropType.ARRAY)
    public acceptableValueRange?: number[];

    /**
     * This is where verification thresholds are configured. It needs to move, but for now it's a 2D array
     *
     * acceptableRangeByValveState[0b00] will give you acceptable range if both valves closed
     * acceptableRangeByValveState[0b11] will give you acceptable range if both valves open
     * acceptableRangeByValveState[0b10] will give you acceptable range if V2 open V1 closed
     */
    @validate({
        zodValidator: z.array(valueRangeArray).optional(),
    })
    @prop({ type: () => Number, dim: 2, required: false }, PropType.ARRAY)
    public acceptableRangeByValveState?: number[][];

    /**
     * Maximum time after a valve transition for the sensor to reach the configured
     * acceptableRangeByValveState before the verification logic considers the sensor
     * to have settled.
     *
     * During this ramp-up window, the sensor check remains pending. After the window
     * elapses, the verification logic may mark the result as failed or inconclusive,
     * depending on the observed sensor behavior.
     */
    @validate({
        zodValidator: milliseconds.optional(),
    })
    @prop({ type: () => Number, required: false, default: minuteMs * 3 })
    public acceptableRampUpTimeMs: number = minuteMs * 3;

    /**
     * Opt-in trailing-edge ramp-down grace (0 = disabled). Within this window before the open
     * state's scheduled close, verification is held instead of failed, so pressure bleeding down
     * ahead of the close isn't reported as a false failure.
     */
    @validate({
        zodValidator: milliseconds.optional(),
    })
    @prop({ type: () => Number, required: false, default: 0 })
    public acceptableRampDownTimeMs: number = 0;

    /**
     * Enables or disables the above thresholds for verification
     */
    @validate({ zodValidator: z.boolean().optional() })
    @prop({ type: () => Boolean, required: false })
    public useForVerification?: boolean;

    @validate({
        childClass: RecentSensorDataPoint,
        childClassTransform: (object) => object.optional(),
        responseOnly: true,
    })
    @prop({ type: () => RecentSensorDataPoint, required: false })
    lastReading?: RecentSensorDataPoint;

    /**
     * calculated automatically by RefreshSensingPeriod subroutine
     * @type {number}
     */
    @validate({ zodValidator: milliseconds.optional(), responseOnly: true })
    @prop({ type: () => Number, required: false })
    public uplinkPeriod?: number;

    /**
     * calculated automatically by RefreshSensingPeriod subroutine
     * @type {number}
     */
    @validate({ zodValidator: milliseconds.optional(), responseOnly: true })
    @prop({ type: () => Number, required: false })
    public readingPeriod?: number;

    /**
     * calculated automatically by RefreshSensingPeriod subroutine
     * @type {number}
     */
    @validate({ zodValidator: z.number().optional(), responseOnly: true })
    @prop({ type: () => Number, required: false })
    public readingsPerUplink?: number;

    @validate({ zodValidator: z.record(z.string(), z.any()).optional() })
    @prop({
        type: () => Object,
        required: true,
        allowMixed: Severity.ALLOW,
        default: {},
    })
    public userMetadata?: Record<string, any> = {};

    static getters = {} as const satisfies Record<string, (self: SensorSpecifier) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<SensorSpecifierSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<SensorSpecifier, SensorSpecifierFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

SensorSpecifier.dtoValidators = getZodSchemaFromClass(
    SensorSpecifier,
) as unknown as DtoValidatorTypes<SensorSpecifierSchemaTypes>;
